import clsx from "clsx";
import {
  ASSESSMENT_VARIANTS,
  SEARCH_TYPES,
  SEARCH_TYPES_ARRAY,
  SOLUTION_MOBILE_CARE,
  SOLUTION_SHORT_TERM_STATIC_CARE,
  typeOfCareToRights,
} from "core/consts";
import {
  isAuctionRunning,
  isCareSearch,
  isHospitalSearch,
  isRehabSearch,
} from "core/model/auctions";
import {
  checkAllowedDuplicateSearchType,
  getSolutionsForPatientType,
  getSpecializations,
  showMultipleSpecializations,
  showSpecializations,
} from "core/model/patients";
import { OntologyType } from "core/model/utils/ontologies";
import {
  useGetOntologies,
  useGetOntology,
} from "core/model/utils/ontologies/hooks";
import { getValues } from "core/model/utils/ontologies/utils";
import { getQueryVariable } from "core/model/utils/urls";
import {
  Auction,
  Careseeker,
  GetOntologyType,
  Patient,
  SearchType,
} from "core/types";
import { getUnixTime } from "date-fns";
import { Banner } from "ds/ui";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import DatePickerInputField from "ds_legacy/components/DatePickerInputField";
import InfoCaption from "ds_legacy/components/InfoCaption";
import ConnectedRadioChipGroup from "ds_legacy/components/RadioChipGroup";
import RadioGroup from "ds_legacy/components/RadioGroup";
import { SelectConnectedWithForm } from "ds_legacy/components/Select";
import ConnectedOntologySelectInput from "ds_legacy/components/SelectInput/Ontology";
import TextInputField from "ds_legacy/components/TextInputField";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_14,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import OntologyCheckboxGroup from "dsl/atoms/OntologyCheckboxGroup";
import { AsteriskExplained } from "dsl/molecules/AsteriskExplained";
import ExternalIdKisField from "dsl/molecules/ExternalIdKisField";
import { FormWatcher, Show } from "react-forms-state";
import { useFormRendererContext } from "react-forms-state/src/Context";
import { useLocation } from "react-router-dom";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { defaultSolutionsForSearchType } from "./modelDefinition";

export const textLabelWidth = dp(350);

export const solutionSortOrder = Object.fromEntries([
  ["1", 2],
  ["2", 3],
  ["3", 6],
  ["4", 4],
  ["5", 5],
  ["8", 1],
]);

export const patientTypeSortOrder = Object.fromEntries([
  ["1", 1],
  ["2", 2],
]);

function searchTypeDisabled({
  careseeker,
  patient,
  roles,
  searchType,
}: {
  careseeker: Careseeker;
  patient?: Patient;
  roles?: number[];
  searchType: SearchType;
}): { disable: boolean; hide: boolean } {
  if (!roles && isCareSearch(searchType))
    return { disable: false, hide: false };

  const duplicateAllowed = checkAllowedDuplicateSearchType({
    auctions: patient?.auctions,
    searchType,
  });

  if (!duplicateAllowed) return { disable: true, hide: false };

  const disabledByRoles = !typeOfCareToRights[searchType]?.some(
    (right) => roles?.includes(right),
  );

  const disabledByCareseeker = !careseeker.patient_types?.includes(searchType);

  const disabledSearchType = disabledByRoles || disabledByCareseeker;

  if (!disabledByRoles && disabledByCareseeker) {
    console.warn(
      `user with role for search type: ${searchType} for careseeker ${careseeker.id} without available patient type in: ${careseeker.patient_types}`,
    );
  }
  if (
    (
      [SEARCH_TYPES.HOME_CARE, SEARCH_TYPES.MEDICAL_SUPPLIES] as number[]
    ).includes(searchType)
  ) {
    return { disable: disabledByRoles, hide: disabledByCareseeker };
  }
  return { disable: disabledSearchType, hide: false };
}

const showFlexibleStartDate = (searchType: SearchType): boolean => {
  return ([SEARCH_TYPES.CARE, SEARCH_TYPES.HOSPITAL] as number[]).includes(
    searchType,
  );
};

const formatStation = ({ description = "", id = -1, name = "" }) => ({
  id,
  label: `${name}${description ? ` - ${description}` : ""}`,
  value: id,
});

export function getPatientTypeOptions({
  careseeker,
  getOntology,
  patient,
  roles,
  translations,
}: {
  careseeker: Careseeker | null;
  getOntology: GetOntologyType;
  patient?: Patient;
  roles?: number[];
  translations: Translations;
}) {
  if (!careseeker) return [];

  return SEARCH_TYPES_ARRAY.map((searchType) => {
    const { disable, hide } = searchTypeDisabled({
      careseeker,
      patient,
      roles,
      searchType,
    });
    if (hide) return null;
    return {
      disabled: disable,
      name: getOntology({ key: searchType, type: "patientType" }),
      tooltipText: disable ? translations.patient.upgradePlan : undefined,
      value: searchType,
    };
  }).truthy();
}

export const getSolutionOptions = (patientType: SearchType) =>
  getValues("solution").filter((sol) =>
    getSolutionsForPatientType(patientType).includes(sol),
  );

export function useFilteredOptions({
  exclude,
  include,
  type,
}: {
  exclude?: number[] | null;
  include?: number[] | null;
  type: OntologyType;
}) {
  const getOntologies = useGetOntologies();
  let values = getOntologies({ sort: true, type });

  if (include) {
    values = values.filter((v) => include?.includes(v.value));
  }

  if (exclude) {
    values = values.filter((v) => exclude && !exclude.includes(v.value));
  }
  return values.map(({ name: label, value }) => ({
    id: value,
    label,
    value,
  }));
}

function SpecializationSelect({
  careseeker,
  required,
  searchType,
}: {
  careseeker: Careseeker;
  required?: boolean;
  searchType: SearchType;
}) {
  const translations = useTranslations();
  const specializations = getSpecializations({
    careseeker,
    searchType,
  });
  const showMultiple = showMultipleSpecializations({ searchType });

  const options = useFilteredOptions({
    include: specializations,
    type: "specializations",
  });

  return (
    <SelectConnectedWithForm
      elementName="specializations"
      label={
        showMultiple ? translations.ontologies.specializations.key : undefined
      }
      multiple={showMultiple}
      options={options}
      placeholder={translations.general.selectDefaultPlaceholderShort}
      required={required}
      width="100%"
    />
  );
}

function ShortAssessmentSelect() {
  const translations = useTranslations();

  return (
    <RadioGroup
      elementName="assessment_variant"
      label={translations.patient.complexityPatient}
      labelStyle={{ fontSize: FONT_SIZE_14 }}
      marginBox={margin(1, 0, 0)}
      marginWrapper={margin(1, 0, 0, -0.5)}
      options={[
        {
          id: 1,
          label: translations.careseeker.shortMobileAssessment,
          // @ts-ignore
          value: "mobile-short",
        },
        {
          id: 2,
          label: translations.careseeker.longMobileAsssessment,
          // @ts-ignore
          value: "",
        },
      ]}
    />
  );
}

function SolutionsSelect({ searchType }: { searchType: SearchType }) {
  const translations = useTranslations();

  return (
    <OntologyCheckboxGroup
      elementName="solutions"
      include={getSolutionOptions(searchType)}
      label={translations.patient.solutionLabel}
      labelStyle={{
        fontSize: FONT_SIZE_14,
        margin: margin(0),
      }}
      order={solutionSortOrder}
      required
      sideMutation={(value, mutateElement) => {
        mutateElement(null, "assessment_variant");
      }}
      type="solution"
      wrapperStyle={{ margin: margin(0) }}
    />
  );
}

function SearchTypeField({
  careseeker,
  isCreating,
  roles,
}: {
  careseeker: Careseeker;
  isCreating: boolean;
  roles: Array<number>;
}) {
  const { resetValidation } = useFormRendererContext();
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const patientOptions = getPatientTypeOptions({
    careseeker,
    getOntology,
    roles,
    translations,
  });

  const sideMutation = (
    newValue: SearchType,
    mutateElement: (s: any, k: string) => void,
  ) => {
    resetValidation([
      "specializations",
      "solutions",
      "start_date",
      "care_duration_in_days",
    ]);

    mutateElement(defaultSolutionsForSearchType(newValue), "solutions");
    mutateElement(null, "care_duration_in_days");

    const today = getUnixTime(new Date());
    const patientTypeSpecializations = getSpecializations({
      careseeker,
      searchType: newValue,
    });

    if (isHospitalSearch(newValue)) {
      mutateElement(today, "start_date");
    }

    if (patientTypeSpecializations.length == 1) {
      mutateElement(
        {
          id: patientTypeSpecializations[0],
          label: getOntology({
            key: patientTypeSpecializations[0],
            type: "specializations",
          }),
          value: patientTypeSpecializations[0],
        },
        "specializations",
      );
    } else {
      mutateElement(null, "specializations");
    }
  };

  return (
    <ConnectedRadioChipGroup
      disabled={!isCreating}
      elementName="search_type"
      formLabelSx={{
        fontSize: FONT_SIZE_14,
        padding: padding(0),
      }}
      id="search_type"
      label={translations.patient.patientType}
      options={patientOptions}
      radioGroupSx={{
        margin: margin(1, 0, 0),
        width: textLabelWidth,
      }}
      required
      sideMutation={sideMutation}
      sortOrder={patientTypeSortOrder}
    />
  );
}

export function PatientCreateForm({
  auction,
  careseeker,
  isCreating,
  roles,
}: {
  auction?: Auction;
  careseeker: Careseeker;
  isCreating: boolean;
  roles: Array<number>;
}) {
  const translations = useTranslations();
  const location = useLocation();
  const externalIdQueryVar =
    getQueryVariable(location.search, "external_id") ||
    auction?.patient?.external_id;

  const searchStarted = auction && isAuctionRunning(auction.status);
  const stationOptions = careseeker.stations_full?.map(formatStation) ?? [];

  return (
    <div className="flex max-w-xl flex-col space-y-4 px-4 pb-8">
      {isCreating && (
        <>
          <AsteriskExplained customMargin={margin(1, 0, 0, 0)} />
          <Title style={{ margin: padding(1, 0) }}>
            {translations.patient.createPatient}
          </Title>
        </>
      )}

      <Subheading bold margin={margin(1, 0)}>
        {translations.patient.hospitalStay}
      </Subheading>

      <div className="flex flex-col space-y-2">
        <ExternalIdKisField
          careseeker={careseeker}
          externalIdQueryVar={externalIdQueryVar}
          textLabelWidth={textLabelWidth}
        />

        {careseeker.stations_full && (
          <div data-testid="station">
            <SelectConnectedWithForm
              elementName="station_full_id"
              fontSize={FONT_SIZE_14}
              label={translations.patient.station}
              options={stationOptions}
              placeholder={translations.general.selectDefaultPlaceholderShort}
              sideMutation={(
                value: { id: number } | undefined,
                mutateElement: (v: any, name: string) => void,
              ) => {
                if (value) {
                  const station = careseeker.stations_full?.find(
                    ({ id }) => id == value.id,
                  );
                  if (station?.phone_number) {
                    mutateElement(
                      station.phone_number,
                      "doctor_in_charge_in_hospital_phone",
                    );
                  }
                }
              }}
              value={stationOptions.length === 1 && stationOptions}
              width={textLabelWidth}
              withPortal
            />
          </div>
        )}
        <div>
          <TextInputField
            elementName="doctor_in_charge_in_hospital_phone"
            hasCustomValidation
            label={translations.patient.stationPhoneNumber}
            marginOverride={margin(1, 0)}
            width={textLabelWidth}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-2">
        <Subheading bold margin={margin(0)}>
          {translations.patient.transfer}
        </Subheading>
        {searchStarted && (
          <InfoCaption text={translations.patient.transferChanges} />
        )}
      </div>
      <div>
        <SearchTypeField
          careseeker={careseeker}
          isCreating={isCreating}
          roles={roles}
        />
      </div>
      <FormWatcher watchPath="search_type">
        {({ watchedValue: searchType }) => {
          const isSearchTypeHidden = [
            SEARCH_TYPES.MEDICAL_SUPPLIES,
            SEARCH_TYPES.HOME_CARE,
          ].includes(searchType);
          return (
            <>
              {searchType && (
                <div hidden={isSearchTypeHidden}>
                  <SolutionsSelect searchType={searchType} />
                </div>
              )}

              <FormWatcher watchPath="solutions">
                {({ watchedValue: solutions }) => {
                  const showSpecialization = showSpecializations({
                    searchType,
                  });
                  const showShortMobileAssessmentSelect =
                    solutions?.length == 1 &&
                    solutions[0] === SOLUTION_MOBILE_CARE;

                  return (
                    <>
                      <div
                        hidden={!showSpecialization}
                        style={{ width: textLabelWidth }}
                      >
                        <SpecializationSelect
                          careseeker={careseeker}
                          required
                          searchType={searchType}
                        />
                      </div>
                      {isRehabSearch(searchType) && (
                        <>
                          <div>
                            <DatePickerInputField
                              key="operation_date"
                              elementName="operation_date"
                              hasCustomValidation
                              inputSx={{
                                margin: margin(0),
                                width: textLabelWidth,
                              }}
                              label={
                                translations.patient.medicalDiagnosis
                                  .operationDate
                              }
                              required
                            />
                          </div>
                          <div>
                            <TextInputField
                              elementName="operation_description"
                              label={
                                translations.patient.medicalDiagnosis
                                  .operationDescription
                              }
                              marginOverride={margin(0)}
                              multiline
                              placeholder={
                                translations.patient.medicalDiagnosis
                                  .operationDescriptionPlaceholder
                              }
                              width={textLabelWidth}
                            />
                          </div>
                        </>
                      )}
                      <div>
                        <DatePickerInputField
                          key="start_date"
                          disablePast
                          elementName="start_date"
                          inputSx={{
                            margin: margin(0),
                            width: textLabelWidth,
                          }}
                          label={translations.patient.expectedStartDate}
                          required
                        />
                      </div>
                      <div hidden={!isRehabSearch(searchType)}>
                        <DatePickerInputField
                          key="discharge_date"
                          elementName="discharge_date"
                          hasCustomValidation
                          inputSx={{
                            margin: margin(0),
                            width: textLabelWidth,
                          }}
                          label={translations.patient.expectedDischargeDate}
                        />
                      </div>
                      {showFlexibleStartDate(searchType) && (
                        <Show elementName="start_date_flexible">
                          <div>
                            <CheckboxInputField
                              elementName="start_date_flexible"
                              label={translations.patient.flexibleDate}
                              margin={margin(0, 0, 0, -1.5)}
                            />
                          </div>
                        </Show>
                      )}
                      {isCareSearch(searchType) && (
                        <>
                          <div style={{ width: textLabelWidth }}>
                            <ConnectedOntologySelectInput
                              elementName="care_duration_in_days"
                              fullWidth
                              label={translations.patient.careDuration}
                              placeholder={translations.patient.careDuration}
                              required
                              size="small"
                              style={{ border: "2px solid red" }}
                              type="careDurations"
                            />
                          </div>
                          <div
                            hidden={
                              !solutions?.includes(
                                SOLUTION_SHORT_TERM_STATIC_CARE,
                              )
                            }
                          >
                            <CheckboxInputField
                              elementName="interested_long_term_stay"
                              label={
                                translations.patient.interestedStayLongTerm
                              }
                              margin={margin(0, 0, 0, -1.5)}
                            />
                          </div>
                        </>
                      )}
                      <div
                        className={clsx("hidden flex-col", {
                          "!flex": showShortMobileAssessmentSelect,
                        })}
                      >
                        <ShortAssessmentSelect />
                      </div>
                      <div
                        className={clsx("hidden flex-col space-y-4", {
                          "!flex": isRehabSearch(searchType),
                        })}
                      >
                        <div>
                          <CheckboxInputField
                            elementName="direct_transfer_necessary"
                            label={translations.patient.directTransferNecessary}
                          />
                        </div>
                        <div>
                          <CheckboxInputField
                            customValue={ASSESSMENT_VARIANTS.ELECTIVE_REHAB}
                            elementName="assessment_variant"
                            label={
                              translations.patient
                                .electivePatientNotInHospitalYet
                            }
                          />
                        </div>
                        <Banner
                          color="primary"
                          message={
                            translations.careproviderApp.settings.payers
                              .infofieldRehabShortInsurances
                          }
                          withIcon={false}
                        />
                      </div>
                    </>
                  );
                }}
              </FormWatcher>
            </>
          );
        }}
      </FormWatcher>
    </div>
  );
}
